import { useTranslate } from '@growtherapy/i18n';
import { Body, Button, Display, Label } from '@growtherapy/sprout-ui';

import flowerShadowBackground from '../../assets/backgrounds/flower-shadow.jpg';
import { LanguageSwitcherWrapper } from '../../components/LanguageSwitcherWrapper';
import { Navbar } from '../../components/Navbar';
import { PageContainer } from '../../components/PageContainer';
import { PageProviders } from '../../components/PageProviders';
import { Theme } from '../../state/theme';
import { useTrackSoftBlockGetStartedView } from './useSoftBlockTracking';

export function Page({ providerName, onPrimaryAction }: { providerName: string; onPrimaryAction: () => void }) {
  const t = useTranslate();
  useTrackSoftBlockGetStartedView();

  const title = t('telehealth-core:preSessionMeasures.helpProviderUnderstandHowYoureDoing', { providerName });
  const body = t('telehealth-core:preSessionMeasures.completeABriefCheckIn');
  const ctaLabel = t('telehealth-core:preSessionMeasures.getStarted');

  return (
    <PageProviders theme={Theme.LIGHT}>
      {/* Side-by-side from `md` up; narrower screens stack the image below. */}
      <PageContainer className='md:flex-row'>
        <div className='flex w-full flex-col md:w-1/2'>
          <Navbar languageSwitcherComponent={LanguageSwitcherWrapper} />

          <div className='flex flex-1 flex-col justify-end px-6 pb-12 md:pb-24 lg:px-12 lg:pb-24 motion-safe:animate-[appear_2s_cubic-bezier(0.16,1,0.3,1)]'>
            <div className='mb-component-150 gap-component-75 flex flex-col'>
              <Label.Small className='text-neutral-700 rebrand:text-text-subtle'>
                {t('telehealth-core:preSessionMeasures.beforeYourSession')}
              </Label.Small>

              {/* Smaller heading only while side-by-side in the narrow half (md–lg);
                  full size when stacked (full width) or when there's room at `lg`. */}
              <Display.Small className='text-neutral-800 rebrand:text-text-default hidden md:block lg:hidden'>
                {title}
              </Display.Small>
              <Display.Default className='text-neutral-800 rebrand:text-text-default block md:hidden lg:block'>
                {title}
              </Display.Default>

              <Body.Default className='text-neutral-700 rebrand:text-text-subtle'>{body}</Body.Default>
            </div>

            <Button onClick={onPrimaryAction} use='primary' className='self-start'>
              {ctaLabel}
            </Button>
          </div>
        </div>

        {/* Decorative image: right half from `md` up, bottom on narrower screens.
            flex-1 + min-h-0 + object-cover lets it crop to leftover space without
            ever forcing the page to scroll. */}
        <div className='min-h-0 flex-1 md:w-1/2 md:flex-none'>
          <img src={flowerShadowBackground} alt='' className='h-full w-full object-cover' />
        </div>
      </PageContainer>
    </PageProviders>
  );
}
